import { DataTable } from '@/components/DataTable';
import { OneLineText } from '@/components/OneLineText';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { displayError } from '@/lib/error';
import { capitalizeFirstLetter, downloadFilefromBuffer, formatPhoneNumber } from '@/lib/utils';
import { getAllVendors } from '@/services/Network';
import { useExportVendorsCsv } from '@/services/vendorService';
import type { VendorResponse } from '@/types/vendor.types';
import { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';

export const ExportVendorDialog = ({ onClose }: { onClose: () => void }) => {
  const [data, setData] = useState<VendorResponse[]>([]);
  const { mutate: getDownloadUrl, status } = useExportVendorsCsv();

  const handleDownloadCSV = () => {
    const today = new Date();
    const fileName = `Vendor_Master_List_${today.getFullYear()}${(today.getMonth() + 1)
      .toString()
      .padStart(2, '0')}${today.getDate().toString().padStart(2, '0')}`;
    getDownloadUrl(void 0, {
      onSuccess: (data) => {
        downloadFilefromBuffer(data.downloadUrl, fileName, 'CSV');
      },
      onError: (e) => {
        displayError(e, 5000);
      },
    });
  };

  const columns: ColumnDef<VendorResponse>[] = [
    {
      accessorKey: 'name',
      header: 'Vendor Name',
      size: 180,
      cell: ({ row }) => {
        return <OneLineText className="mr-5 min-w-[150px]" text={row.original.name ?? '-'} />;
      },
    },
    {
      accessorKey: 'address',
      header: 'Address',
      cell: ({ row }) => {
        return <OneLineText className="mr-5 min-w-[100px]" text={row.original.address1} />;
      },
    },
    {
      accessorKey: 'city',
      header: 'City',
      size: 120,
      cell: ({ row }) => {
        return <OneLineText className="mr-5 min-w-[100px]" text={row.original.city ?? '-'} />;
      },
    },
    {
      accessorKey: 'state',
      header: 'State',
      cell: ({ row }) => {
        return <OneLineText className="mr-5 min-w-[100px]" text={row.original.state ?? '-'} />;
      },
    },
    {
      accessorKey: 'zipCode',
      header: 'Zip Code',
      cell: ({ row }) => {
        return <OneLineText className="mr-5 min-w-[100px]" text={row.original.zipCode ?? '-'} />;
      },
    },
    {
      accessorKey: 'phone',
      header: 'Phone Number',
      cell: ({ row }) => {
        return <div className="min-w-[200px] text-sm">{formatPhoneNumber(row.original.phone)}</div>;
      },
    },
    {
      accessorKey: 'email',
      header: 'Email Address',
      cell: ({ row }) => {
        return <OneLineText className="mr-5 min-w-[200px]" text={row.original.email ?? '-'} />;
      },
    },
    {
      accessorKey: 'taxId',
      header: 'Tax ID',
      cell: ({ row }) => {
        return <OneLineText className="mr-5 min-w-[100px] text-sm" text={row.original.taxId ?? '-'} />;
      },
    },
    {
      accessorKey: 'coverageAreas',
      header: 'Coverage Area(s)',
      cell: ({ row }) => {
        return (
          <OneLineText className="mr-5 min-w-[150px] text-sm" text={row.original.coverageAreas?.join(', ') ?? '-'} />
        );
      },
    },
    {
      accessorKey: 'notes',
      header: 'Notes',
      cell: ({ row }) => {
        return <OneLineText className="mr-5 min-w-[100px] text-sm" text={row.original.notes ?? '-'} />;
      },
    },
    {
      accessorKey: 'fees',
      header: 'Fees',
      cell: ({ row }) => {
        return <div className="min-w-[100px] text-sm">{row.original.fees}</div>;
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        return <div className="min-w-[100px] text-sm">{capitalizeFirstLetter(row.original.status)}</div>;
      },
    },
  ];

  return (
    <Sheet modal={true} open={true}>
      <SheetContent
        hideCloseButton={true}
        className="md:w-[80vw] w-[90vw] md:gap-12 md:max-h-[80vh] max-h-[100vh] h-fit flex flex-col scrollbar-hide focus:outline-none rounded-lg gap-4 p-8"
        tabIndex={-1}
        side={'center'}
      >
        <div className="flex-grow min-h-0 gap-4 md:gap-8 flex flex-col">
          <SheetTitle>Preview - Vendor Master List</SheetTitle>
          <DataTable
            columns={columns}
            apiGet={getAllVendors}
            showPagination={false}
            stickyHeader
            className=" max-h-[44vh] min-h-[10px] h-fit scrollbar-hide"
            setRows={setData}
            showNRecords
            customPageSize={1000}
          />
        </div>
        <div className="w-full flex flex-col gap-4 md:flex-row justify-end ">
          <Button
            type="button"
            variant="outline"
            className="text-primary w-[75px] h-[36px] py-2 px-4 rounded-md focus:outline-none border-primary hover:bg-[#EDF5FF]"
            onClick={onClose}
          >
            Cancel
          </Button>

          <Button
            variant="outline"
            className="text-[#fff] h-[36px] w-fit min-w-[135px] py-2 px-4 rounded-md bg-primary border-primary hover:bg-[#12375C] hover:text-white"
            onClick={handleDownloadCSV}
            disabled={data.length === 0 || status === 'pending'}
            isLoading={status === 'pending'}
          >
            Download CSV
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
